// Event-type-tailored onboarding for the AI invitation designer. Keyed by the
// canonical English event-type label (see lib/eventTypes.ts). Each entry gives
// the host a one-line tip, recommended vibes, and clickable example prompts so
// the first generation lands close to what fits their occasion.

export type AiTutorialEntry = {
  tip: { en: string; ka: string }
  /** Vibe ids (see lib/aiDesign.ts VIBES) that usually work for this type. */
  vibes: string[]
  prompts: Array<{ en: string; ka: string }>
}

const DEFAULT_TUTORIAL: AiTutorialEntry = {
  tip: {
    en: 'Pick a vibe, optionally describe a detail, and generate — every design is a unique background with matching fonts.',
    ka: 'აირჩიე განწყობა, სურვილისამებრ აღწერე დეტალი და დააგენერირე — ყოველი დიზაინი უნიკალური ფონია შესაბამისი შრიფტებით.',
  },
  vibes: ['elegant', 'festive', 'minimal'],
  prompts: [
    {
      en: 'warm golden light, soft abstract shapes, celebratory mood',
      ka: 'თბილი ოქროსფერი შუქი, რბილი აბსტრაქტული ფორმები, საზეიმო განწყობა',
    },
    {
      en: 'subtle texture, deep evening tones, a hint of shimmer',
      ka: 'ნაზი ტექსტურა, ღრმა საღამოს ტონები, მსუბუქი ბზინვარება',
    },
  ],
}

export const AI_TUTORIALS: Record<string, AiTutorialEntry> = {
  Wedding: {
    tip: {
      en: 'For weddings, soft romantic or elegant vibes work best — think florals, silk and candlelight rather than bright party colours.',
      ka: 'ქორწილისთვის საუკეთესოა რომანტიული ან ელეგანტური განწყობა — ყვავილები, აბრეშუმი და სანთლის შუქი, კაშკაშა ფერების ნაცვლად.',
    },
    vibes: ['romantic', 'elegant', 'dreamy'],
    prompts: [
      {
        en: 'ivory silk, blush roses, soft candlelight, gold accents',
        ka: 'სპილოსძვლისფერი აბრეშუმი, ვარდები, სანთლის ნაზი შუქი, ოქროსფერი აქცენტები',
      },
      {
        en: 'olive branches, warm dusk sky, delicate watercolor wash',
        ka: 'ზეთისხილის რტოები, თბილი დაისის ცა, ნაზი აკვარელი',
      },
    ],
  },
  'Birthday Party': {
    tip: {
      en: 'Birthdays love energy — playful or festive vibes with bold colour make the invitation feel like the party already started.',
      ka: 'დაბადების დღეს ენერგია უხდება — მხიარული ან სადღესასწაულო განწყობა თამამი ფერებით მოსაწვევს ზეიმის განცდას აძლევს.',
    },
    vibes: ['playful', 'festive', 'bold'],
    prompts: [
      {
        en: 'confetti burst, vibrant gradient, balloons floating up',
        ka: 'კონფეტის აფეთქება, ხასხასა გრადიენტი, აფრენილი ბუშტები',
      },
      {
        en: 'neon glow, sprinkles, joyful colour splash',
        ka: 'ნეონის ნათება, ფერადი ნამცხვრის მარცვლები, სიხარულის ფერები',
      },
    ],
  },
  'Baby Shower': {
    tip: {
      en: 'Keep it soft: dreamy pastels, clouds and gentle light suit a baby shower far better than saturated colour.',
      ka: 'სინაზეა მთავარი: ნაზი პასტელები, ღრუბლები და რბილი შუქი ბეიბი შაუერს ბევრად უხდება, ვიდრე მკვეთრი ფერები.',
    },
    vibes: ['dreamy', 'minimal', 'playful'],
    prompts: [
      {
        en: 'pastel clouds, tiny stars, soft morning light',
        ka: 'პასტელური ღრუბლები, პაწაწინა ვარსკვლავები, დილის რბილი შუქი',
      },
      {
        en: 'gentle watercolor, powder blue and blush pink',
        ka: 'ნაზი აკვარელი, ცისფერი და ვარდისფერი ტონები',
      },
    ],
  },
  'Corporate Event': {
    tip: {
      en: 'For corporate events choose minimal or bold — clean geometry and restrained colour read as professional and premium.',
      ka: 'კორპორატიულისთვის აირჩიე მინიმალური ან თამამი — სუფთა გეომეტრია და თავშეკავებული ფერები პროფესიონალურად და პრემიუმად აღიქმება.',
    },
    vibes: ['minimal', 'bold', 'luxury'],
    prompts: [
      {
        en: 'clean geometric lines, deep navy, subtle metallic accent',
        ka: 'სუფთა გეომეტრიული ხაზები, მუქი ლურჯი, ნაზი მეტალის აქცენტი',
      },
      {
        en: 'abstract architecture, glass and light, modern gradient',
        ka: 'აბსტრაქტული არქიტექტურა, მინა და შუქი, თანამედროვე გრადიენტი',
      },
    ],
  },
  'Dinner Party': {
    tip: {
      en: 'Dinner parties shine with elegant or luxury vibes — candlelight, linen and wine tones set the table before anyone arrives.',
      ka: 'სადილს ელეგანტური ან ლუქს განწყობა უხდება — სანთლის შუქი, სელი და ღვინის ტონები წინასწარ ქმნის ატმოსფეროს.',
    },
    vibes: ['elegant', 'luxury', 'minimal'],
    prompts: [
      {
        en: 'candlelit table, linen texture, deep wine tones',
        ka: 'სანთლებით განათებული სუფრა, სელის ტექსტურა, ღრმა ღვინისფერი',
      },
      {
        en: 'olive branches, rustic ceramics, warm evening glow',
        ka: 'ზეთისხილის რტოები, კერამიკა, თბილი საღამოს ნათება',
      },
    ],
  },
  Anniversary: {
    tip: {
      en: 'Anniversaries call for romance with a touch of gold — elegant and romantic vibes with warm, nostalgic light.',
      ka: 'იუბილეს რომანტიკა და ოქროს შეხება უხდება — ელეგანტური და რომანტიული განწყობა თბილი, ნოსტალგიური შუქით.',
    },
    vibes: ['romantic', 'elegant', 'luxury'],
    prompts: [
      {
        en: 'golden hour glow, roses, champagne shimmer',
        ka: 'ოქროსფერი საათის ნათება, ვარდები, შამპანურის ბზინვარება',
      },
      {
        en: 'vintage warmth, soft bokeh lights, deep red accents',
        ka: 'ვინტაჟური სითბო, ბოკეს რბილი შუქები, ღრმა წითელი აქცენტები',
      },
    ],
  },
  Graduation: {
    tip: {
      en: 'Graduations suit bold or festive vibes — triumphant colour, light rays and a sense of a big new chapter.',
      ka: 'გამოსაშვებს თამამი ან სადღესასწაულო განწყობა უხდება — ტრიუმფული ფერები, შუქის სხივები და ახალი თავის დაწყების განცდა.',
    },
    vibes: ['bold', 'festive', 'minimal'],
    prompts: [
      {
        en: 'rising light rays, confetti, deep academic blue and gold',
        ka: 'ამომავალი შუქის სხივები, კონფეტი, ღრმა ლურჯი და ოქროსფერი',
      },
      {
        en: 'starry sky, upward motion, celebratory sparkle',
        ka: 'ვარსკვლავიანი ცა, აღმავალი მოძრაობა, საზეიმო ნაპერწკლები',
      },
    ],
  },
  'Holiday Celebration': {
    tip: {
      en: 'Holiday invites work best festive or luxury — rich seasonal colour, sparkle and cosy warmth.',
      ka: 'სადღესასწაულო მოსაწვევს ფესტიური ან ლუქს განწყობა უხდება — მდიდარი სეზონური ფერები, ბრჭყვიალა და სითბო.',
    },
    vibes: ['festive', 'luxury', 'dreamy'],
    prompts: [
      {
        en: 'snowfall, warm lights, deep green and crimson',
        ka: 'თოვა, თბილი შუქები, მუქი მწვანე და ალისფერი',
      },
      {
        en: 'fireworks over a night sky, golden sparkle',
        ka: 'ფოიერვერკი ღამის ცაზე, ოქროსფერი ნაპერწკლები',
      },
    ],
  },
  'Charity Event': {
    tip: {
      en: 'Charity invites earn trust with minimal or elegant vibes — warm, human imagery and one clear accent colour beat loud decoration.',
      ka: 'საქველმოქმედოს ნდობას მინიმალური ან ელეგანტური განწყობა მატებს — თბილი, ადამიანური სახეები და ერთი მკაფიო აქცენტი ხმაურიან დეკორს სჯობს.',
    },
    vibes: ['minimal', 'elegant', 'dreamy'],
    prompts: [
      {
        en: 'open hands and soft warm light, single hopeful accent colour',
        ka: 'გახსნილი ხელები და რბილი თბილი შუქი, ერთი იმედიანი აქცენტი',
      },
      {
        en: 'gentle sunrise gradient, clean space for a cause message',
        ka: 'ნაზი ამომავალი მზის გრადიენტი, სუფთა ადგილი მთავარი გზავნილისთვის',
      },
    ],
  },
  Trip: {
    tip: {
      en: 'Trips look best with dreamy or minimal vibes — horizon, distance and open sky sell the journey better than clutter.',
      ka: 'მოგზაურობას ოცნებისეული ან მინიმალური განწყობა უხდება — ჰორიზონტი, სივრცე და ღია ცა მოგზაურობას უკეთ გადმოსცემს.',
    },
    vibes: ['dreamy', 'minimal', 'bold'],
    prompts: [
      {
        en: 'mountain horizon at dawn, soft haze, wide open sky',
        ka: 'მთის ჰორიზონტი გათენებისას, ნისლი, ფართო ღია ცა',
      },
      {
        en: 'vintage map textures, compass lines, warm travel tones',
        ka: 'ვინტაჟური რუკის ტექსტურა, კომპასის ხაზები, თბილი სამოგზაურო ტონები',
      },
    ],
  },
  'Surprise Gift': {
    tip: {
      en: 'A surprise wants playful or dreamy vibes — ribbon, sparkle and a hint of something hidden just about to open.',
      ka: 'სიურპრიზს მხიარული ან ოცნებისეული განწყობა უხდება — ლენტი, ბრჭყვიალა და განცდა, რომ რაღაც სასიამოვნო იხსნება.',
    },
    vibes: ['playful', 'dreamy', 'festive'],
    prompts: [
      {
        en: 'silk ribbon and soft sparkle, a gift about to be opened',
        ka: 'აბრეშუმის ლენტი და ნაზი ბრჭყვიალა, გასახსნელად მზა საჩუქარი',
      },
      {
        en: 'warm bokeh lights, pastel confetti, tender celebratory glow',
        ka: 'თბილი ბოკეს შუქები, პასტელური კონფეტი, ნაზი საზეიმო ნათება',
      },
    ],
  },
  Other: DEFAULT_TUTORIAL,
}

export const getAiTutorial = (eventType?: string): AiTutorialEntry =>
  (eventType && AI_TUTORIALS[eventType]) || DEFAULT_TUTORIAL
